import React from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import { trackItemListView, trackItemSelect } from "../utils/analytics";

const ProductPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [products, setProducts] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const categories = ['전체', '베스트', '신상품', '프론트', '백엔드', '마케팅', '기타', '할인상품'];
    const [activeCategory, setActiveCategory] = React.useState('전체');
    const sortOptions = ['인기순',  '후기순', '금액 낮은 순', '금액 높은 순'];
    const [sortOption, setSortOption] = React.useState('인기순');

    React.useEffect(() => {
        // 카테고리 정보 받아오기
        if (location.state) {

            if (location.state === 'NCOMPANY' || location.state === 'KCOMPANY') {
                setActiveCategory('전체');
            } else{
                setActiveCategory(categoryEnum2[location.state]);
            }

        }

        const getProductList = async () => {
            try {
                const response = await fetch("/api/items", {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });

                const data = await response.json();
                if (response.ok) {
                    setProducts(data.items);
                    trackItemListView(Array.isArray(data.items) ? data.items.length : 0);
                    setLoading(false);
                } else {
                    setError(data.message || "상품 데이터를 불러올 수 없습니다.");
                    setLoading(false);
                }
            } catch (err) {
                setError("데이터 로딩 중 오류가 발생했습니다.");
                setLoading(false);
            }
        };

        getProductList();
    }, []);

    const getProductDetail = (itemId, itemName) => {
        trackItemSelect(itemName);
        navigate(`/products/${itemId}`);
        window.scrollTo(0, 0);
    };

    const categoryEnum = {
        '베스트': 'BEST',
        '신상품': 'NEW',
        '프론트': 'NCOMPANY',
        '백엔드': 'KCOMPANY',
        '마케팅': 'MARKETING',
        '기타': 'ETC',
        '할인상품': 'DISCOUNTED'
    };

    const categoryEnum2 = {
        'BEST': '베스트',
        'NEW': '신상품',
        'NCOMPANY': '프론트',
        'KCOMPANY': '백엔드',
        'MARKETING': '마케팅',
        'ETC': '기타',
        'DISCOUNTED': '할인상품'
    }

    // 카테고리 필터링
    const filteredProducts = activeCategory === '전체'
        ? products
        : products.filter(product => product.category === categoryEnum[activeCategory]);

    // 정렬 함수
    const sortedProducts = React.useMemo(() => {
        let sorted = [...filteredProducts];
        switch (sortOption) {
            case '인기순':
                sorted.sort((a, b) => b.rating - a.rating); // 예: popularity 필드
                break;
            // case '가격순':
            case '금액 낮은 순':
                sorted.sort((a, b) => a.salePrice - b.salePrice);
                break;
            case '금액 높은 순':
                sorted.sort((a, b) => b.salePrice - a.salePrice);
                break;
            case '후기순':
                sorted.sort((a, b) => b.reviewCount - a.reviewCount); // 예: reviewCount 필드
                break;
            default:
                break;
        }
        return sorted;
    }, [filteredProducts, sortOption]);

    if (loading) {
        return (
            <div className="sch-wrap" style={{ padding: "72px 32px" }}>
                <p className="sch-mono">상품 목록을 불러오는 중…</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="sch-wrap" style={{ padding: "72px 32px" }}>
                <p className="sch-mono">{error}</p>
            </div>
        );
    }

    return (
        <>
            <div className="sch-ph">
                <div className="sch-wrap">
                    <div className="sch-no">INDEX — 제품·서비스</div>
                    <h1>제품 색인</h1>
                    <p className="sc-body-lg">자체 개발해 판매·납품 중인 자동화 도구 전체 목록입니다. 행을 누르면 상세로 이어집니다.</p>
                </div>
            </div>

            <div className="sch-wrap" style={{ paddingTop: "36px", paddingBottom: "72px" }}>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "26px" }}>
                    {categories.map((category) => (
                        <button
                            key={category}
                            className={`sc-chip${activeCategory === category ? " sc-chip--active" : ""}`}
                            style={{ borderRadius: 0 }}
                            onClick={() => setActiveCategory(category)}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                <div className="sch-sortbar">
                    {sortOptions.map((option) => (
                        <button
                            key={option}
                            className={sortOption === option ? "sch-active" : ""}
                            onClick={() => setSortOption(option)}
                        >
                            {option}
                        </button>
                    ))}
                </div>

                {sortedProducts.length === 0 ? (
                    <div className="sc-empty">
                        <div className="sc-empty__icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 7h18l-1.5 12.5a2 2 0 0 1-2 1.5H6.5a2 2 0 0 1-2-1.5L3 7Z" />
                                <path d="M8 7V5a4 4 0 0 1 8 0v2" />
                            </svg>
                        </div>
                        <div className="sc-empty__title">현재 판매 중인 상품이 없습니다</div>
                        <div className="sc-empty__desc">다른 카테고리를 선택하거나 맞춤 개발로 문의해 주세요.</div>
                        <button className="sc-btn sc-btn--full" onClick={() => navigate("/custom-product-first")}>
                            맞춤 개발 문의
                        </button>
                    </div>
                ) : (
                    <div className="sch-pi">
                        {sortedProducts.map((product, index) => (
                            <div
                                key={product.itemId ?? index}
                                className="sch-pirow"
                                role="button"
                                onClick={() => getProductDetail(product.itemId, product.itemName)}
                            >
                                <span className="sch-code">P-{String(index + 1).padStart(2, "0")}</span>
                                <span className="sch-nm">{product.itemName}</span>
                                <span className="sch-ds">{product.itemDescription}</span>
                                <span className="sch-tg">
                                    {categoryEnum2[product.category] ?? product.category}
                                    {product.discountRate ? ` / ${product.discountRate}%` : ""}
                                </span>
                                <span className="sch-ar">→</span>
                            </div>
                        ))}
                    </div>
                )}

                <p className="sch-mono" style={{ marginTop: "22px" }}>
                    * 목록에 없는 도구는 대부분 맞춤 개발로 해결됩니다 —{" "}
                    <button
                        className="sch-inlinelink"
                        onClick={() => { navigate("/custom-product-first"); window.scrollTo(0, 0); }}
                    >
                        문의하기 ↗
                    </button>
                </p>
            </div>
        </>
    );
};

export default ProductPage;
